import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LabResultsTab = ({ onUploadResult, onOrderLab }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const labResults = [
    {
      id: "LAB-2025-001",
      testName: "Complete Blood Count (CBC)",
      orderedBy: "Dr. Sarah Johnson",
      orderedDate: "2025-09-18",
      collectedDate: "2025-09-19",
      resultDate: "2025-09-20",
      status: "Completed",
      priority: "Routine",
      lab: "HealthLab Central",
      results: [
        { parameter: "White Blood Cells", value: "7.2", unit: "K/uL", range: "4.0-11.0", status: "normal" },
        { parameter: "Red Blood Cells", value: "4.8", unit: "M/uL", range: "4.2-5.4", status: "normal" },
        { parameter: "Hemoglobin", value: "14.2", unit: "g/dL", range: "12.0-16.0", status: "normal" },
        { parameter: "Hematocrit", value: "42.1", unit: "%", range: "36.0-46.0", status: "normal" },
        { parameter: "Platelets", value: "285", unit: "K/uL", range: "150-450", status: "normal" }
      ],
      documents: ["CBC_Report_Sept2025.pdf", "FHIR_CBC_Data.json"],
      notes: "All values within normal limits. No signs of infection or anemia."
    },
    {
      id: "LAB-2025-002",
      testName: "Comprehensive Metabolic Panel",
      orderedBy: "Dr. Michael Chen",
      orderedDate: "2025-09-15",
      collectedDate: "2025-09-16",
      resultDate: "2025-09-17",
      status: "Completed",
      priority: "Routine",
      lab: "MedTest Laboratory",
      results: [
        { parameter: "Glucose", value: "95", unit: "mg/dL", range: "70-100", status: "normal" },
        { parameter: "BUN", value: "18", unit: "mg/dL", range: "7-20", status: "normal" },
        { parameter: "Creatinine", value: "1.0", unit: "mg/dL", range: "0.6-1.2", status: "normal" },
        { parameter: "Sodium", value: "140", unit: "mEq/L", range: "136-145", status: "normal" },
        { parameter: "Potassium", value: "4.2", unit: "mEq/L", range: "3.5-5.0", status: "normal" },
        { parameter: "Chloride", value: "102", unit: "mEq/L", range: "98-107", status: "normal" }
      ],
      documents: ["CMP_Report_Sept2025.pdf", "FHIR_CMP_Data.json"],
      notes: "Excellent metabolic panel results. Kidney function normal, electrolytes balanced."
    },
    {
      id: "LAB-2025-003",
      testName: "Lipid Panel",
      orderedBy: "Dr. Sarah Johnson",
      orderedDate: "2025-09-10",
      collectedDate: "2025-09-12",
      resultDate: "2025-09-13",
      status: "Completed",
      priority: "Routine",
      lab: "HealthLab Central",
      results: [
        { parameter: "Total Cholesterol", value: "185", unit: "mg/dL", range: "<200", status: "normal" },
        { parameter: "LDL Cholesterol", value: "110", unit: "mg/dL", range: "<100", status: "high" },
        { parameter: "HDL Cholesterol", value: "55", unit: "mg/dL", range: ">40", status: "normal" },
        { parameter: "Triglycerides", value: "120", unit: "mg/dL", range: "<150", status: "normal" }
      ],
      documents: ["Lipid_Panel_Sept2025.pdf", "FHIR_Lipid_Data.json"],
      notes: "LDL cholesterol slightly elevated. Recommend dietary modifications and follow-up in 3 months."
    },
    {
      id: "LAB-2025-004",
      testName: "Thyroid Function Panel",
      orderedBy: "Dr. Emily Rodriguez",
      orderedDate: "2025-09-05",
      collectedDate: "2025-09-06",
      resultDate: "2025-09-08",
      status: "Completed",
      priority: "Routine",
      lab: "EndoLab Diagnostics",
      results: [
        { parameter: "TSH", value: "2.1", unit: "mIU/L", range: "0.4-4.0", status: "normal" },
        { parameter: "Free T4", value: "1.3", unit: "ng/dL", range: "0.8-1.8", status: "normal" },
        { parameter: "Free T3", value: "3.2", unit: "pg/mL", range: "2.3-4.2", status: "normal" }
      ],
      documents: ["Thyroid_Panel_Sept2025.pdf", "FHIR_Thyroid_Data.json"],
      notes: "Thyroid function within normal limits. No evidence of hypo or hyperthyroidism."
    }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-success text-success-foreground';
      case 'pending': return 'bg-warning text-warning-foreground';
      case 'in-progress': return 'bg-primary text-primary-foreground';
      case 'cancelled': return 'bg-error text-error-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getResultStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-success';
      case 'high': return 'text-warning';
      case 'low': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getResultStatusIcon = (status) => {
    switch (status) {
      case 'normal': return 'CheckCircle';
      case 'high': return 'ArrowUp';
      case 'low': return 'ArrowDown';
      case 'critical': return 'AlertTriangle';
      default: return 'Minus';
    }
  };

  const filteredResults = labResults?.filter(result => {
    const matchesSearch = result?.testName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         result?.orderedBy?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         result?.lab?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || result?.status?.toLowerCase() === filterStatus?.toLowerCase();
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-clinical">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search lab results by test name, provider, or lab..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-4 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
            </select>
            
            <Button variant="outline" iconName="Upload" iconPosition="left" onClick={onUploadResult}>
              Upload Result
            </Button>
            
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onOrderLab}>
              Order Lab
            </Button>
          </div>
        </div>
      </div>
      {/* Lab Results List */}
      <div className="space-y-4">
        {filteredResults?.map((result) => (
          <div key={result?.id} className="bg-card border border-border rounded-lg shadow-clinical">
            {/* Result Header */}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-accent" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{result?.testName}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(result?.status)}`}>
                        {result?.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="User" size={14} />
                        Ordered by {result?.orderedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        Collected: {new Date(result.collectedDate)?.toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Building" size={14} />
                        {result?.lab}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="Download">
                    Download PDF
                  </Button>
                  <Button variant="outline" size="sm" iconName="Code">
                    Export FHIR
                  </Button>
                </div>
              </div>
            </div>

            {/* Result Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-foreground mb-4">Test Results</h4>
                  <div className="space-y-3">
                    {result?.results?.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-foreground">{item?.parameter}</span>
                            <Icon 
                              name={getResultStatusIcon(item?.status)} 
                              size={16} 
                              className={getResultStatusColor(item?.status)} 
                            />
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">{item?.value} {item?.unit}</span>
                            <span>Range: {item?.range}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Clinical Notes</h4>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground">{result?.notes}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Available Documents</h4>
                    <div className="space-y-2">
                      {result?.documents?.map((document, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                          <div className="flex items-center gap-2">
                            <Icon 
                              name={document?.includes('.pdf') ? 'FileText' : 'Code'} 
                              size={16} 
                              className="text-muted-foreground" 
                            />
                            <span className="text-sm text-foreground">{document}</span>
                          </div>
                          <Button variant="ghost" size="sm" iconName="Download">
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Timeline</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Ordered:</span>
                        <span className="text-foreground">{new Date(result.orderedDate)?.toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Collected:</span>
                        <span className="text-foreground">{new Date(result.collectedDate)?.toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Results:</span>
                        <span className="text-foreground">{new Date(result.resultDate)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredResults?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center shadow-clinical">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No lab results found</h3>
          <p className="text-muted-foreground mb-6">No lab results match your current search criteria.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" iconName="Upload" iconPosition="left" onClick={onUploadResult}>
              Upload Result
            </Button>
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onOrderLab}>
              Order New Lab
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabResultsTab;